"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";

const steps = [
  {
    number: "01",
    title: "Define your audience",
    description:
      "Describe your ideal respondent in natural language. Terac translates your criteria into precise targeting parameters.",
  },
  {
    number: "02",
    title: "AI recruits & screens",
    description:
      "AI agents source candidates 24/7, verify credentials, and screen for qualification. Only verified experts make it through.",
  },
  {
    number: "03",
    title: "Experts delivered",
    description:
      "Qualified experts are routed directly to your research platform via API. You handle the interview, we handle the panel.",
  },
];

export default function MarketResearchHowItWorks() {
  return (
    <section className="py-20">
      <Container>
        {/* Main card */}
        <div className="border-marketing-border bg-marketing-cream-light rounded-[20px] border px-6 py-12 md:px-12 lg:px-20 lg:py-16">
          {/* Header */}
          <div className="mb-16 flex flex-col items-center gap-8">
            <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
              <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
                How it works
              </p>
            </div>
            <h2 className="text-marketing-text max-w-[600px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
              From brief to verified experts
            </h2>
            <p className="text-marketing-text max-w-[500px] text-center text-lg font-medium">
              Integrate Terac as your panel backbone and focus on what you do
              best.
            </p>
          </div>

          {/* Steps */}
          <div className="mx-auto max-w-3xl">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, transform: "translateY(20px)" }}
                whileInView={{ opacity: 1, transform: "translateY(0px)" }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="border-marketing-border flex gap-6 border-b py-8 last:border-b-0"
              >
                <span className="text-marketing-blue font-mono text-lg font-medium">
                  {step.number}
                </span>
                <div>
                  <h3 className="text-marketing-text mb-2 text-xl font-medium">
                    {step.title}
                  </h3>
                  <p className="text-marketing-text-muted text-base leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 flex justify-center">
            <Link
              href="/contact"
              className="text-marketing-blue inline-flex items-center gap-2 text-base font-medium"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span>Talk to sales</span>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
}
